/**
 * Feature-Level Error Boundary
 *
 * Re-export as `ErrorBoundary` from every layout route to prevent a crash in
 * one feature from unmounting the root layout and navigation.
 *
 * React Router picks up named `ErrorBoundary` exports from route modules
 * and renders them in place of the crashed subtree — keeping sibling
 * features and the parent layout alive.
 */
/* eslint-disable i18next/no-literal-string -- Error boundaries are fallback UI; i18n may be unavailable */
import { routes } from '@letsgethome/routes';
import { plaintextString } from '@letsgethome/types/branded';

import { useReportedRouteError } from '../hooks/useReportedRouteError';
import { Link } from '../i18n/routing';

export function FeatureErrorBoundary() {
  // The router's verdict when that is what arrived, ALREADY REPORTED — one
  // read, so the heading below and the level it was logged at cannot disagree
  // (they did: `is404` was computed for the heading and never consulted by the
  // logger). The reporting itself — an effect, not a render side effect; a
  // routed verdict kept whole instead of `new Error(String(error))`; the level
  // split by status — lives in `useReportedRouteError`, which the root boundary
  // shares, because the two copies of this logic drifting is precisely how the
  // sibling boundary kept all three defects after this one was fixed.
  const routeError = useReportedRouteError('FeatureErrorBoundary');
  const is404 = routeError?.status === 404;

  return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <div className="text-center">
        <h1 className="text-4xl font-bold">
          {is404 ? plaintextString('404') : plaintextString('Error')}
        </h1>
        <p className="mt-4 text-lg text-muted-foreground">
          {is404
            ? plaintextString('Page not found.')
            : plaintextString('Something went wrong. Please try again.')}
        </p>
        <Link href={routes.home()} className="mt-6 inline-block text-primary underline">
          Go home
        </Link>
      </div>
    </div>
  );
}
